import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Send, Youtube, Instagram, Facebook, Twitter } from 'lucide-react';
import { motion } from 'framer-motion';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      alert('Thank you for your message! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 2000);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      value: "admin@vr-media.in",
      link: "mailto:admin@vr-media.in"
    },
    {
      icon: Phone,
      title: "Phone",
      value: "+91 8754469694",
      link: "tel:+918754469694"
    },
    {
      icon: MapPin,
      title: "Office",
      value: "Chennai, Tamil Nadu",
      link: "#"
    }
  ];

  const socialLinks = [
    { name: 'YouTube', icon: Youtube, url: 'https://www.youtube.com/@vrmedianetwork', color: 'text-red-500' },
    { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/vrmedianetwork', color: 'text-pink-500' },
    { name: 'Facebook', icon: Facebook, url: 'https://facebook.com/vrmedianetwork', color: 'text-blue-500' },
    { name: 'Twitter', icon: Twitter, url: 'https://twitter.com/vrmedianetwork', color: 'text-blue-400' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Helmet>
        <title>Contact Us - VR Media Network</title>
        <meta name="description" content="Get in touch with VR Media Network. We'd love to hear from you about collaborations, feedback, or general inquiries." />
        <meta property="og:title" content="Contact Us - VR Media Network" />
        <meta property="og:description" content="Get in touch with VR Media Network for collaborations, feedback, or general inquiries." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vrmedianetwork.com/contact" />
        <link rel="canonical" href="https://vrmedianetwork.com/contact" />
      </Helmet>

      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="pt-24 pb-16 bg-vr-darker"
      >
        <div className="container-custom">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold font-display text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              We'd love to hear from you! Whether you have a question, want to collaborate, 
              or just want to say hello, we're here to help.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Contact Form & Info */}
      <section className="section-padding bg-vr-dark">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-vr-darker rounded-xl p-8 border border-gray-800">
              <h2 className="text-2xl font-bold text-white mb-6">Send us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-vr-dark border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-vr-yellow focus:border-transparent"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-vr-dark border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-vr-yellow focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-vr-dark border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-vr-yellow focus:border-transparent"
                  >
                    <option value="">Select a reason</option>
                    <option value="collaboration">Collaboration</option>
                    <option value="feedback">Feedback</option>
                    <option value="general">General Inquiry</option>
                    <option value="support">Support</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-vr-dark border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-vr-yellow focus:border-transparent resize-none"
                    placeholder="Tell us how we can help you..."
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-vr-yellow text-vr-dark font-semibold py-3 px-6 rounded-lg hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-vr-dark border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              {/* Contact Information */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>
                <div className="space-y-4">
                  {contactInfo.map((info, index) => (
                    <a
                      key={index}
                      href={info.link}
                      className="flex items-center space-x-4 p-4 bg-vr-darker rounded-lg border border-gray-800 hover:border-vr-yellow transition-colors duration-200"
                    >
                      <div className="flex-shrink-0">
                        <info.icon size={24} className="text-vr-yellow" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-400">{info.title}</div>
                        <div className="text-white">{info.value}</div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Social Media */}
              <div>
                <h3 className="text-xl font-bold text-white mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  {socialLinks.map((social) => (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-3 bg-vr-darker border border-gray-800 rounded-lg hover:border-current transition-colors duration-200 ${social.color}`}
                    >
                      <social.icon size={24} />
                    </a>
                  ))}
                </div>
              </div>

              {/* Office Hours */}
              <div className="bg-vr-darker rounded-xl p-6 border border-gray-800">
                <h3 className="text-xl font-bold text-white mb-4">Office Hours</h3>
                <div className="space-y-2 text-gray-300">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span>9:00 AM - 6:00 PM IST</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span>10:00 AM - 4:00 PM IST</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Embedded Content */}
      <section className="section-padding bg-vr-darker">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-6">
              Check Out Our Latest Content
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Watch our most recent videos and see what we've been working on.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* YouTube Playlist */}
            <div className="bg-vr-dark rounded-xl p-6 border border-gray-800">
              <h3 className="text-xl font-bold text-white mb-4">Latest Videos</h3>
              <div className="aspect-video bg-gray-800 rounded-lg overflow-hidden">
                <iframe
                  src="https://www.youtube.com/embed/videoseries?list=PLx0sYbCqOb8TBPRdmBHs5Iftvv9TPboYG"
                  title="VR Media Network Latest Videos"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>

            {/* Map */}
            <div className="bg-vr-dark rounded-xl p-6 border border-gray-800">
              <h3 className="text-xl font-bold text-white mb-4">Our Location</h3>
              <div className="aspect-video bg-gray-800 rounded-lg overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d497214.8258500001!2d79.786164!3d13.0826802!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5265ea4f7d3361%3A0xf8c28feb10302ee!2sChennai%2C%20Tamil%20Nadu!5e0!3m2!1sen!2sin!4v1640995200000!5m2!1sen!2sin"
                  title="VR Media Network Office Location - Chennai, Tamil Nadu"
                  className="w-full h-full"
                  frameBorder="0"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-vr-dark">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Find answers to common questions about working with us.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-vr-darker rounded-xl p-6 border border-gray-800">
              <h3 className="text-lg font-bold text-white mb-3">How can I collaborate with VR Media Network?</h3>
              <p className="text-gray-300">
                We're always open to collaboration opportunities! Send us a message through our contact form 
                with details about your proposal, and we'll get back to you within 24-48 hours.
              </p>
            </div>
            
            <div className="bg-vr-darker rounded-xl p-6 border border-gray-800">
              <h3 className="text-lg font-bold text-white mb-3">What types of content do you create?</h3>
              <p className="text-gray-300">
                We create diverse content across four main channels: cooking (VR Kitchen), 
                spirituality (VR Religious), wellness (VR Wellness), and storytelling (VR Short Stories).
              </p>
            </div>
            
            <div className="bg-vr-darker rounded-xl p-6 border border-gray-800">
              <h3 className="text-lg font-bold text-white mb-3">How often do you post new content?</h3>
              <p className="text-gray-300">
                Each channel has its own posting schedule, typically 2-3 videos per week. 
                Subscribe to our channels to never miss new content!
              </p>
            </div>
            
            <div className="bg-vr-darker rounded-xl p-6 border border-gray-800">
              <h3 className="text-lg font-bold text-white mb-3">Can I suggest content ideas?</h3>
              <p className="text-gray-300">
                Absolutely! We love hearing from our community. Send us your ideas through 
                our contact form, and we'll consider them for future content.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-vr-red/20 via-vr-yellow/20 to-vr-blue/20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-6">
            Ready to Connect?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            We're excited to hear from you and explore how we can work together to create amazing content.
          </p>
          <a href="#contact-form" className="btn-primary inline-flex items-center space-x-2">
            <span>Send us a Message</span>
            <Send size={20} />
          </a>
        </div>
      </section>
    </motion.div>
  );
};

export default ContactPage;
